import React, { Component } from 'react';
import dateFormat from 'dateformat';

import {
    FAILED_ORDER_STATUS,
    FAILED_ORDER_REASON,
    getFailedOrders,
    reprocessFailedOrder,
    ignoreFailedOrder,
} from '../utils/mlApi';

// Panel de recuperación de órdenes de MercadoLibre que no se pudieron ingresar (ENI-64).
// El admin ve por qué cayeron (qué publicación falta vincular), vincula el producto y
// fuerza el reprocesamiento desde acá (sin re-disparar el webhook a mano).
// Extraído de la vieja FailedMlOrdersPage al pasar la pantalla a "Integración ML".

const FILTERS = [
    { key: 'pending', label: 'Pendientes' },
    { key: 'recovered', label: 'Recuperadas' },
    { key: 'ignored', label: 'Ignoradas' },
    { key: '', label: 'Todas' },
];

class MlFailedOrdersComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            data: [],
            loaded: false,
            statusFilter: 'pending',
            busyId: null,
            message: null,
        };
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        this.setState({ loaded: false });
        getFailedOrders(this.state.statusFilter)
            .then((rows) => this.setState({ data: rows, loaded: true }))
            .catch((error) => this.setState({
                loaded: true,
                message: { type: 'danger', text: error.message },
            }));
    }

    setFilter = (key) => this.setState({ statusFilter: key, message: null }, this.load);

    reprocess = (item) => {
        const missing = (item.missingItems || []);
        if (missing.length > 0) {
            const ok = window.confirm(
                `Esta orden todavía tiene publicaciones sin vincular (${missing.join(', ')}).\n` +
                `Si reprocesás ahora va a volver a fallar. ¿Continuar igual?`
            );
            if (!ok) return;
        }
        this.setState({ busyId: item.id, message: null });
        reprocessFailedOrder(item.id)
            .then((result) => {
                const r = result || {};
                const message = r.recovered
                    ? { type: 'success', text: `Orden #${item.id} recuperada → compra ${r.purchaseId}.` }
                    : { type: 'warning', text: `Orden #${item.id}: sigue fallando. Revisá que la publicación esté vinculada.` };
                this.setState({ busyId: null, message }, this.load);
            })
            .catch((error) => this.setState({
                busyId: null,
                message: { type: 'danger', text: error.message || 'No se pudo reprocesar.' },
            }));
    }

    ignore = (item) => {
        if (!window.confirm(`¿Marcar la orden #${item.id} como ignorada?`)) return;
        this.setState({ busyId: item.id });
        ignoreFailedOrder(item.id)
            .then(() => this.setState({ busyId: null }, this.load))
            .catch((error) => this.setState({
                busyId: null,
                message: { type: 'danger', text: error.message },
            }));
    }

    renderRow = (item, index) => {
        const badge = FAILED_ORDER_STATUS[item.status] || { cls: 'badge-light', text: item.status };
        const missing = item.missingItems || [];
        const isPending = item.status === 'pending';
        return (
            <tr key={item.id} style={{ background: index % 2 === 0 ? '#fff' : '#fcfcfc' }}>
                <td>{item.created_at ? dateFormat(item.created_at, 'dd/mm/yyyy HH:MM') : '—'}</td>
                <td>
                    <div>{item.order_resource}</div>
                    {item.pack_id && <small className="text-muted">pack {item.pack_id}</small>}
                </td>
                <td>{FAILED_ORDER_REASON[item.reason] || item.reason}</td>
                <td>
                    {(item.items || []).length === 0 && <span className="text-muted">—</span>}
                    {(item.items || []).map((mlu) => {
                        const stillMissing = missing.includes(mlu);
                        return (
                            <span key={mlu} className={`badge ${stillMissing ? 'badge-danger' : 'badge-success'} mr-1`}
                                title={stillMissing ? 'Sin vincular' : 'Ya vinculada'}>
                                {mlu}
                            </span>
                        );
                    })}
                </td>
                <td className="text-center">{item.attempts}</td>
                <td>
                    <span className={`badge ${badge.cls}`}>{badge.text}</span>
                    {item.recovered_purchase_id && <div><small className="text-muted">compra {item.recovered_purchase_id}</small></div>}
                </td>
                <td>
                    {isPending ? (
                        <div className="btn-group btn-group-sm" role="group">
                            <button type="button" className="btn btn-primary"
                                disabled={this.state.busyId === item.id}
                                onClick={() => this.reprocess(item)}>
                                {this.state.busyId === item.id ? '...' : 'Reprocesar'}
                            </button>
                            <button type="button" className="btn btn-white"
                                disabled={this.state.busyId === item.id}
                                onClick={() => this.ignore(item)}>
                                Ignorar
                            </button>
                        </div>
                    ) : <span className="text-muted">—</span>}
                </td>
            </tr>
        );
    }

    render() {
        const { data, loaded, message } = this.state;

        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom">
                    <ul className="nav nav-pills">
                        {FILTERS.map((f) => (
                            <li className="nav-item" key={f.key || 'all'}>
                                <button type="button"
                                    className={`btn btn-sm mr-1 ${this.state.statusFilter === f.key ? 'btn-primary' : 'btn-white'}`}
                                    onClick={() => this.setFilter(f.key)}>
                                    {f.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="card-body p-0">
                    {message && (
                        <div className={`alert alert-${message.type} m-3`}>{message.text}</div>
                    )}
                    <p className="text-muted px-3 pt-3 mb-2">
                        Ventas de ML que no se pudieron ingresar. Vinculá la publicación faltante (en rojo) y reprocesá.
                    </p>
                    {!loaded ? (
                        <p className="px-3 pb-3">Cargando…</p>
                    ) : data.length === 0 ? (
                        <p className="px-3 pb-3">No hay órdenes en este estado.</p>
                    ) : (
                        <table className="table mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th scope="col" className="border-0">Fecha</th>
                                    <th scope="col" className="border-0">Orden</th>
                                    <th scope="col" className="border-0">Motivo</th>
                                    <th scope="col" className="border-0">Publicaciones</th>
                                    <th scope="col" className="border-0 text-center">Intentos</th>
                                    <th scope="col" className="border-0">Estado</th>
                                    <th scope="col" className="border-0">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((item, index) => this.renderRow(item, index))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        );
    }
}

export default MlFailedOrdersComponent;
